import { Link } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import ProgressRing from '../ui/ProgressRing';
import SectionHeader from '../ui/SectionHeader';
import { api } from '../../lib/api';
import { normalizeBooks } from '../../lib/normalizers';
import { useState, useEffect } from 'react';
import { useAuthStore } from '../../context/useAuthStore';

export default function ContinueReading() {
  const user = useAuthStore((s) => s.user);
  const [inProgress, setInProgress] = useState([]);

  useEffect(() => {
    if (!user) {
      setInProgress([]);
      return;
    }
    let cancelled = false;

    // Fetch reading progress instead of wishlist
    Promise.all([
      api.get('/reading/progress'),
      api.get('/reading/library')
    ])
      .then(([progressData, libraryData]) => {
        if (cancelled) return;

        // Get books with progress > 0 and < 100 (currently reading). A book
        // can have more than one progress entry (e.g. leftover duplicate
        // rows from before the backend deduped them) — collapse those to
        // the furthest one reached so the same book never renders twice.
        const bestProgressByBook = new Map();
        progressData.progress
          .filter(p => p.progress > 0 && p.progress < 100)
          .forEach(p => {
            const existing = bestProgressByBook.get(p.book);
            if (!existing || p.progress > existing) {
              bestProgressByBook.set(p.book, p.progress);
            }
          });

        const inProgressBooks = Array.from(bestProgressByBook.entries())
          .slice(0, 3)
          .map(([bookId, percent]) => {
            const book = libraryData.books.find(b => b._id === bookId);
            return book ? { book, percent } : null;
          })
          .filter(Boolean);

        setInProgress(inProgressBooks);
      })
      .catch(() => {
        if (!cancelled) {
          setInProgress([]);
        }
      });

    return () => { cancelled = true; };
  }, [user]);

  if (!user) return null;

  return (
    <section className="container-app py-10">
      {inProgress.length > 0 && (
        <>
          <SectionHeader chapter="02" title="Continue Reading" subtitle="Pick up right where you left off." />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {inProgress.map(({ book, percent }) => (
              <Link
                key={book.id}
                to={`/library`}
                className="card-surface group flex items-center gap-4 p-4 transition-shadow hover:shadow-soft-lg"
              >
                <img src={book.cover} alt={book.title} className="h-20 w-14 shrink-0 rounded-lg object-cover shadow-spine" />
                <div className="min-w-0 flex-1">
                  <p className="line-clamp-1 font-display font-semibold text-slate-800 dark:text-white group-hover:text-primary-600 transition-colors">
                    {book.title}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{book.author?.name}</p>
                  <p className="mt-1 text-xs font-medium text-primary-600 dark:text-primary-300">Resume reading</p>
                </div>
                <ProgressRing percent={percent} />
              </Link>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
